// 'use client' is load-bearing, not decoration: this module calls React hooks,
// and it is reachable from the `@auctogon/ui/web` barrel. A Server Component
// importing ANY symbol from that barrel pulls this file into the RSC graph,
// where hooks don't exist — `next build` then fails with "You're importing a
// module that depends on useEffect into a React Server Component module".
// The client-side mirror of sharing.md § 3's root-barrel hygiene rule; guarded
// by web-barrel-client-directive.test.ts (BL-1124).
'use client';

// Shared one-time-passcode entry primitive (BUG-362). A controlled row of
// single-character cells with the standard OTP keyboard behavior — numeric
// auto-advance, backspace-back, paste-fill, and fire-on-complete — extracted
// from three hand-rolled copies (bidder PhoneVerificationModal, bidder
// ChangePhone/CodeStep, admin verify-phone page) so the behavior lives once.
//
// Presentational only: it owns behavior + structure, not the pixels. Each
// surface passes its own `inputClassName` so its exact look is preserved.

import { useRef, useEffect, type KeyboardEvent, type ClipboardEvent, type CSSProperties } from 'react';

export interface OTPInputProps {
  /** Number of cells. Defaults to 6. */
  length?: number;
  /** Controlled value — one string (0–1 chars) per cell. Length must equal `length`. */
  value: string[];
  /** Called with the next per-cell array whenever a cell changes or a paste fills cells. */
  onChange: (next: string[]) => void;
  /** Fires once the final cell is filled (by typing the last digit or pasting a full code). */
  onComplete?: (code: string) => void;
  /** Disables every cell (e.g. while a verify call is in flight). */
  disabled?: boolean;
  /** Focus the first cell on mount. */
  autoFocus?: boolean;
  /** Drives the focus-ring color via the `--tw-ring-color` CSS var (paired with a `focus:ring-*` class). */
  brandColor?: string;
  /** Marks every cell `aria-invalid` — pair with an error-variant `inputClassName`. */
  invalid?: boolean;
  /**
   * Extra inline style merged onto every cell, over the `brandColor` ring var
   * (e.g. the bidder auth pages' `authFocusStyle()` `--auth-focus`/`--auth-ring` vars).
   */
  inputStyle?: CSSProperties;
  /** Full class string applied to every cell — the surface owns its own look. */
  inputClassName?: string;
  /** Wrapper class string (layout/gap). */
  containerClassName?: string;
  /** Accessible label prefix for each cell, e.g. "Verification code". */
  ariaLabel?: string;
}

const DEFAULT_INPUT_CLASS =
  'h-12 w-10 rounded-lg border border-zinc-300 text-center text-lg font-semibold ' +
  'focus:border-zinc-500 focus:outline-none disabled:opacity-50';

/**
 * Controlled OTP cell row. Keeps no internal value state — the parent owns the
 * `string[]`. Manages only per-cell DOM focus. Numeric-only: non-digit
 * keystrokes are ignored. Pasting a code fills from the first cell regardless
 * of which cell received the paste.
 */
export function OTPInput({
  length = 6,
  value,
  onChange,
  onComplete,
  disabled = false,
  autoFocus = false,
  brandColor,
  invalid = false,
  inputStyle,
  inputClassName = DEFAULT_INPUT_CLASS,
  containerClassName = 'flex justify-center gap-2',
  ariaLabel = 'Verification code',
}: OTPInputProps) {
  const inputs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (autoFocus) inputs.current[0]?.focus();
  }, [autoFocus]);

  /** Numeric-only per-cell handler: stores the last digit, auto-advances, fires onComplete on the final cell. */
  const handleChange = (index: number, raw: string) => {
    const digit = raw.replace(/\D/g, '').slice(-1);
    // A non-digit keystroke (e.g. a letter) yields '' and must not clear a
    // filled cell or move focus — bail so it's a no-op.
    if (!digit && raw !== '') return;
    const next = [...value];
    next[index] = digit;
    onChange(next);
    if (digit && index < length - 1) inputs.current[index + 1]?.focus();
    if (digit && index === length - 1 && next.every((d) => d !== '')) onComplete?.(next.join(''));
  };

  /** Backspace on an already-empty cell jumps focus to the previous cell. */
  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !value[index] && index > 0) inputs.current[index - 1]?.focus();
  };

  /** Paste fills cells from the start; a full-length paste also fires onComplete. */
  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
    if (!pasted) return;
    const next = [...value];
    for (let i = 0; i < pasted.length; i++) next[i] = pasted[i];
    onChange(next);
    inputs.current[Math.min(pasted.length, length - 1)]?.focus();
    if (pasted.length === length) onComplete?.(pasted);
  };

  const ringStyle: CSSProperties | undefined = brandColor
    ? ({ '--tw-ring-color': brandColor } as CSSProperties)
    : undefined;
  const cellStyle: CSSProperties | undefined =
    ringStyle || inputStyle ? { ...ringStyle, ...inputStyle } : undefined;

  return (
    <div className={containerClassName}>
      {Array.from({ length }).map((_, index) => (
        <input
          key={index}
          ref={(el) => {
            inputs.current[index] = el;
          }}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          autoComplete="one-time-code"
          maxLength={1}
          aria-label={`${ariaLabel} digit ${index + 1}`}
          aria-invalid={invalid || undefined}
          value={value[index] ?? ''}
          disabled={disabled}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          className={inputClassName}
          style={cellStyle}
        />
      ))}
    </div>
  );
}

export default OTPInput;
